import { useCallback } from 'react';

interface ShareData {
  title: string;
  text?: string;
  url?: string;
  /** Entity type for OG image support (band, musician, venue, show) */
  entityType?: 'band' | 'musician' | 'venue' | 'show';
  /** Entity ID for OG image support */
  entityId?: string;
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

/**
 * Builds a share URL that goes through the API's OG endpoint for proper social media previews.
 * Crawlers will get proper og:image tags, while users get redirected to the SPA.
 */
function buildShareUrl(data: ShareData): string {
  // If entity info is provided, use the OG endpoint for proper image previews
  if (data.entityType && data.entityId) {
    const entityPath = data.entityType === 'band' ? 'bands' :
                       data.entityType === 'musician' ? 'musicians' :
                       data.entityType === 'venue' ? 'venues' : 'shows';
    return `${API_BASE_URL}/og/${entityPath}/${data.entityId}`;
  }

  // Fall back to provided URL or current location
  return data.url || window.location.href;
}

/**
 * Returns a share function that uses the Web Share API on supported platforms
 * (native share sheet on iOS/Android) and falls back to copying the URL
 * to the clipboard with an optional callback for showing a toast.
 *
 * For entities (bands, musicians, venues, shows), pass entityType and entityId
 * to get proper social media preview images.
 */
export function useShare(onFallback?: (message: string) => void) {
  const share = useCallback(async (data: ShareData) => {
    const shareUrl = buildShareUrl(data);

    if (navigator.share) {
      try {
        // Only share the URL - the OG metadata provides title/description/image as a preview
        // Passing both text and url causes the URL to appear separately in MMS/iMessage
        await navigator.share({ url: shareUrl });
      } catch (err: any) {
        if (err?.name !== 'AbortError') {
          navigator.clipboard?.writeText(shareUrl).catch(() => {});
          onFallback?.('Link Copied');
        }
      }
    } else {
      navigator.clipboard?.writeText(shareUrl).catch(() => {});
      onFallback?.('Link Copied');
    }
  }, [onFallback]);

  return share;
}
